import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Factory, Server, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { fetchIsmPmi } from "@/lib/api";

const fmt = (v) => (v == null ? "—" : Number(v).toFixed(1));

// For PMI, a rising reading is expansionary (green); falling is red.
function Delta({ v }) {
  if (v == null) return <span className="text-zinc-700 tabular-nums text-[11px]">--</span>;
  const cls = v > 0 ? "text-pos" : v < 0 ? "text-neg" : "text-zinc-500";
  const Arrow = v > 0 ? ArrowUp : v < 0 ? ArrowDown : Minus;
  return (
    <span className={`inline-flex items-center gap-0.5 tabular-nums text-[11px] ${cls}`}>
      <Arrow size={9} strokeWidth={2.5} />{Math.abs(v).toFixed(1)}
    </span>
  );
}

function PmiRow({ Icon, label, d }) {
  if (!d) return null;
  const expanding = d.expanding;
  return (
    <div className="flex items-center gap-2 px-3 py-2.5" data-testid={`ism-row-${label.toLowerCase()}`}>
      <Icon size={13} strokeWidth={1.5} className="text-amber-term shrink-0" />
      <span className="text-[11px] uppercase tracking-widest text-zinc-300 w-24 truncate">{label}</span>
      <span className={`text-lg font-semibold tabular-nums leading-none ${expanding ? "text-pos" : "text-neg"}`}>{fmt(d.headline)}</span>
      <span className="ml-auto flex items-center gap-2">
        <Delta v={d.change} />
        <span className={`text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-zinc-700 ${expanding ? "text-pos" : "text-neg"}`}>
          {expanding ? "Expand" : "Contract"}
        </span>
      </span>
    </div>
  );
}

export default function ISMPmiCard() {
  const q = useQuery({
    queryKey: ["ism-pmi"],
    queryFn: fetchIsmPmi,
    staleTime: 1000 * 60 * 60 * 6,
    refetchOnWindowFocus: false,
  });
  const d = q.data;

  return (
    <Link
      to="/ism"
      data-testid="ism-pmi-card"
      className="block border border-zinc-800 bg-[#09090B] hover:border-amber-term/50 transition-colors group"
    >
      <div className="px-3 py-2 border-b border-zinc-800 flex items-center justify-between gap-2">
        <span className="text-[11px] uppercase tracking-widest text-zinc-400 flex items-center gap-1.5 whitespace-nowrap">
          <Factory size={11} strokeWidth={1.5} className="text-amber-term" /> ISM PMI · US
        </span>
        <span className="text-[10px] uppercase tracking-widest text-zinc-600 group-hover:text-amber-term transition-colors">
          {d?.as_of_month ? `${d.as_of_month} · Details ›` : "Details ›"}
        </span>
      </div>

      {q.isLoading ? (
        <div className="h-[100px] flex items-center justify-center text-zinc-600 text-[11px] uppercase tracking-widest">Loading ISM…</div>
      ) : q.isError ? (
        <div className="h-[100px] flex items-center justify-center text-neg text-[11px] uppercase tracking-widest">ISM unavailable</div>
      ) : (
        <div className="divide-y divide-zinc-900">
          <PmiRow Icon={Factory} label="Manufacturing" d={d.manufacturing} />
          <PmiRow Icon={Server} label="Services" d={d.services} />
        </div>
      )}
    </Link>
  );
}
